import { AlertCircle, Loader2, Mail, MessageSquare } from "lucide-react";
import { useRef, useState, useEffect } from "react";

interface VerificationCodeProps {
  onSubmit: (code: string) => void;
  onResendCode: (preferredLoginType?: "email" | "sms") => void;
  loginHint?: string;
  emailHint?: string;
  error?: string;
  isLoading?: boolean;
  verificationType?: "email" | "sms";
  smsLoginSupported?: boolean;
}

export function VerificationCodeVariantA({
  onSubmit,
  onResendCode,
  loginHint = "",
  emailHint = "",
  error,
  verificationType = "email",
  smsLoginSupported = false,
  isLoading = false,
}: VerificationCodeProps) {
  const [code, setCode] = useState(["", "", "", "", "", ""]);
  const [currentVerificationType, setCurrentVerificationType] = useState<"email" | "sms">(verificationType);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Auto-focus first input on mount
  useEffect(() => {
    const timer = setTimeout(() => {
      inputRefs.current[0]?.focus();
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  // Handle input change
  const handleChange = (index: number, value: string) => {
    // Only allow numbers
    if (!/^\d*$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);

    // If we have a value and there's a next input, focus it
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // If all digits are filled, submit the code
    if (newCode.every((digit) => digit) && newCode.join("").length === 6) {
      onSubmit(newCode.join(""));
    }
  };

  // Handle backspace
  const handleKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (e.key === "Backspace") {
      if (!code[index] && index > 0) {
        // If current input is empty and we're not at the first input,
        // focus previous input and clear its value
        const newCode = [...code];
        newCode[index - 1] = "";
        setCode(newCode);
        inputRefs.current[index - 1]?.focus();
      }
    }
  };

  // Handle paste
  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, 6);
    const newCode = [...code];

    for (let i = 0; i < pastedData.length; i++) {
      newCode[i] = pastedData[i];
    }

    setCode(newCode);

    // Focus the next empty input or the last input if all are filled
    const nextEmptyIndex = newCode.findIndex((digit) => !digit);
    const focusIndex = nextEmptyIndex === -1 ? 5 : nextEmptyIndex;
    inputRefs.current[focusIndex]?.focus();

    // Submit if we have a complete code
    if (newCode.every((digit) => digit) && newCode.join("").length === 6) {
      onSubmit(newCode.join(""));
    }
  };

  return (
    <div className="bg-white shadow-xl rounded-2xl overflow-hidden max-w-lg mx-auto">
      <div className="p-6">
      <div className="flex items-center justify-center mb-6">
        <div className="bg-primary-50 rounded-full px-4 py-3 flex items-center gap-2">
          {currentVerificationType === "sms" ? (
            <>
              <MessageSquare className="w-6 h-6 text-primary-600" />{" "}
              <span className="text-primary-700 font-medium">{loginHint}</span>
            </>
          ) : (
            <>
              <Mail className="w-6 h-6 text-primary-600" />
              <span className="text-primary-700 font-medium">{emailHint}</span>
            </>
          )}
          
        </div>
      </div>

        <div className="text-center space-y-3 mb-6">

        <div className="rounded-lg p-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Almost there!
            </h3>
            <p className="text-gray-900 font-medium">
              Enter your verification code to save your responses and complete your application
            </p>
          </div>


          <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
            <p className="text-gray-700 mb-2">
              We found an existing account for{" "}
              <span className="font-medium text-gray-900">{emailHint}</span>{" "}and we need to verify you are the owner of the account
            </p>
          </div>
        </div>

        <div className="flex justify-center gap-2 mb-6">
          {code.map((digit, index) => (
            <input
              key={index}
              ref={(el) => (inputRefs.current[index] = el)}
              type="tel"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={1}
              value={digit}
              onChange={(e) => handleChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              onPaste={handlePaste}
              className={`w-12 h-12 text-center text-2xl font-semibold border rounded-lg 
                focus:ring-2 focus:ring-primary-500 focus:border-primary-500 
                transition-all duration-200
                ${digit ? "bg-primary-50 border-primary-200" : "bg-white"}
                ${error ? "border-red-300 focus:ring-red-500 focus:border-red-500" : "border-gray-200"}`}
              style={{ appearance: "textfield" }}
              aria-label={`Digit ${index + 1} of verification code`}
            />
          ))}
        </div>

        {error && (
          <div className="text-red-500 text-sm mb-4 flex items-center justify-center">
            <AlertCircle className="w-4 h-4 mr-1 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <div className="text-center space-y-4">
          <button
            type="button"
            onClick={() => onSubmit(code.join(""))}
            disabled={!code.every((digit) => digit) || isLoading}
            className="w-full bg-primary-600 text-white rounded-lg px-6 py-3 font-semibold text-lg
              hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 
              focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed 
              transition-all duration-200 shadow-lg hover:shadow-xl"
          >
            Complete Application Now
          </button>

          <div className="flex flex-col space-y-2 mt-6">
            <button
              type="button"
              onClick={() => onResendCode(currentVerificationType)}
              disabled={isLoading}
              className="text-gray-600 hover:text-gray-700 font-medium 
                focus:outline-none focus:ring-2 focus:ring-primary-500 
                focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed 
                transition-colors duration-200 flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Sending...
                </>
              ) : (
                "Resend Code"
              )}
            </button>
            {currentVerificationType === "sms" ? (
              <button
                type="button"
                onClick={() => {
                  setCurrentVerificationType("email");
                  onResendCode("email");
                }}
                className="text-primary-600 hover:text-primary-700 font-medium 
                  focus:outline-none focus:ring-2 focus:ring-primary-500 
                  focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed 
                  transition-colors duration-200"
              >
                Use Email Instead
              </button>
            ) : smsLoginSupported ? (
              <button
                type="button"
                onClick={() => {
                  setCurrentVerificationType("sms");
                  onResendCode("sms");
                }}
                className="text-primary-600 hover:text-primary-700 font-medium 
                  focus:outline-none focus:ring-2 focus:ring-primary-500 
                  focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed 
                  transition-colors duration-200"
              >
                Use SMS Instead
              </button>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
